import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { GoogleMap, MarkerF, Marker, InfoWindow, CircleF } from '@react-google-maps/api';
import './SchoolMaps.scss';
import { MapProps } from '../../Models/UniversityModel';
import SchoolMapicon from '../../Assets/School/SchoolMap.svg';
import shareIcon from '../../Assets/School/shareIcon.svg';
import LikeIcon from '../../Assets/School/schoolLikeIcon.svg';
import locationIcon from '../../Assets/Tutor/location.svg';
import grades from '../../Assets/Tutor/grades.svg';
import curriculum from '../../Assets/Tutor/curriculum.svg';
import {
  GetSessionStorageData,
  SetSessionStorageData,
  encryptPassData,
  nullUndefinedEmptyCheck,
} from '../../Utilities/Helpers/Helper';
import {
  API_END_POINTS,
  AUTHENTICATION,
  GLOBAL_CONSTANTS,
  ROUTES,
  TUTORMAPCONSTANT,
} from '../../Constants/Constants';
import { setLocation, setLocationName, setLoginSignUpOpen } from '../../Redux/Reducer/Reducer';
import { useDispatch, useSelector } from 'react-redux';
import { axiosInstance } from '../../Utilities/Interceptor/Interceptor';
import toast from 'react-hot-toast';
import SchoolFill from '../../Assets/HeartFill/SchoolFill.svg';
import WebShareComponent from '../WebShare/WebShare';
import { useNavigate } from 'react-router-dom';

const SchoolMaps = ({
  zoom,
  radius,
  lat,
  lang,
  containerStyle,
  disable,
  zoomControl,
  clickableIcons,
  draggable,
  getOneTutor,
  searchValue,
  filter,
  initFilter,
  initSort,
  language,
}: MapProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isDragging, setIsDragging] = useState(false);
  const dragTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const isLoaded: boolean = useSelector((state: any) => state.googleMapLoadedReducer.isGoogleMapLoaded);
  const [currentLocation, setCurrentLocation] = useState<any>(null);
  const [selectedLocation, setSelectedLocation] = useState<any>(null);
  const [SchoolList, setSchoolList] = useState<any>([]);
  const [userProfileImg, setUserProfileImg] = useState<string | null>(null);
  const [showMapTooltip, setShowMapTooltip] = useState<boolean>(false);
  const [showMapTooltipMobile, setShowMapTooltipMobile] = useState<boolean>(false);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isMobileView, setIsMobileView] = useState<boolean>(window.innerWidth <= 636);
  const HeaderLocation: any = useSelector((state: any) => state.locationReducer.location);

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth <= 636);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setShowMapTooltipMobile(isMobileView);
  }, [isMobileView]);

  useEffect(() => {
    const location = GetSessionStorageData(AUTHENTICATION.SHARED.SCHOOL_LOCATION);
    if (location) {
      const parsed = JSON.parse(location);
      setCurrentLocation(parsed);
      dispatch(setLocation(parsed));
    } else {
      setCurrentLocation({ lat: parseFloat(lat), lng: parseFloat(lang) });
    }

    const profile = GetSessionStorageData(AUTHENTICATION.SHARED.PROFILE_PICTURE);
    if (nullUndefinedEmptyCheck(profile)) {
      setUserProfileImg(profile);
    }

    if (lat && lang && isLoaded) {
      SchoolListApi();
    }
  }, [lat, lang, isLoaded, dispatch]);

  const payload = () => {
    const priceValue =
      initFilter.price[0] === filter.price[0] && initFilter.price[1] === filter.price[1]
        ? []
        : { min: filter.price[0], max: filter.price[1] };
    const gradeValue =
      initFilter.grade[0] === filter.grade[0] && initFilter.grade[1] === filter.grade[1]
        ? []
        : { min: filter.grade[0], max: filter.grade[1] };

    const sanitizedCurriculum = filter.curriculum?.filter(Boolean).map((c: string) => {
      try {
        const cleaned = c.trim().normalize('NFKD').replace(/[^\x00-\x7F]/g, '');
        return encodeURIComponent(cleaned);
      } catch {
        return '';
      }
    }).filter(Boolean) || [];

    let sanitizedSearch = "";
    try {
      if (searchValue) {
        const cleaned = searchValue.trim().normalize('NFKD').replace(/[^\x00-\x7F]/g, '');
        sanitizedSearch = encodeURIComponent(cleaned);
      }
    } catch (err) {
      console.warn('Invalid search value detected:', err);
    }

    return {
      latitude: lat?.toString() || "",
      longitude: lang?.toString() || "",
      radius: filter.radius,
      search: sanitizedSearch,
      price: priceValue,
      rating: filter.rating || [],
      curriculum: sanitizedCurriculum,
      grade: gradeValue,
    };
  };

  const SchoolListApi = () => {
    const pay = payload();
    axiosInstance
      .post(`${API_END_POINTS.SCHOOLLIST}`, pay)
      .then((Response: any) => {
        setSchoolList(Response?.data?.result?.data || []);
      })
      .catch((error) => {
        console.error("Error fetching school list:", error);
      });
  };

  const navigateDetailedView = (Details: any) => {
    navigate(ROUTES.SCHOOL_DETAILE_VIEW + "?id=" + encryptPassData(Details?.id), {
      state: { tutor_Detailes: Details },
    });
  };

  const mapOptions = {
    disableDefaultUI: disable,
    zoomControl: zoomControl,
    draggable: draggable,
    clickableIcons: clickableIcons,
    disableDoubleClickZoom: disable,
    gestureHandling: "greedy",
    styles: [
      {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
      },
    ],
  };

  useEffect(() => {
    if (initSort?.price || initSort?.rating) {
      handleProceedSort(initSort?.price, initSort?.rating);
    }
  }, [initSort, SchoolList]);

  const handleProceedSort = (checkedPrice: any, checkedRating: any) => {
    const sortedData = [...SchoolList];
    if (checkedPrice === "high") {
      sortedData.sort((a: any, b: any) => b?.least_fee - a?.least_fee);
    } else if (checkedPrice === "low") {
      sortedData.sort((a: any, b: any) => a?.least_fee - b?.least_fee);
    } else if (checkedRating === "high") {
      sortedData.sort((a: any, b: any) => b?.least_rating - a?.least_rating);
    } else if (checkedRating === "low") {
      sortedData.sort((a: any, b: any) => a?.least_rating - b?.least_rating);
    }
    setSchoolList(sortedData);
  };

  const handleChangeFavourites = (val: any, id: any) => {
    const accessToken = GetSessionStorageData(AUTHENTICATION.SHARED.ACCESS_TOKEN);
    if (nullUndefinedEmptyCheck(accessToken)) {
      const payload = {
        service_provider_type: GLOBAL_CONSTANTS.INITIAL_VALUES.FOUR_INT,
        service_provider: id,
        is_favourite: !val,
      };
      axiosInstance
        .post(`${API_END_POINTS.FAVOURITE__ADD}`, payload)
        .then((Response: any) => {
          if (Response?.data?.status) {
            toast.success(Response?.data?.message, {
              position: GLOBAL_CONSTANTS.INITIAL_VALUES.TOAST_POSITION,
            });
            SchoolListApi();
          }
        })
        .catch((error) => {
          console.error("Error updating favorites:", error);
        });
    } else {
      dispatch(setLoginSignUpOpen(GLOBAL_CONSTANTS.INITIAL_VALUES.TRUE));
    }
  };

  const handleMapClick = (e: any) => {
    if (e.latLng) {
      setSelectedLocation(null);
    }
  };

  const reverseGeocode = useCallback(
    (latitude: number, longitude: number) => {
      if (!window.google?.maps) return;
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        { location: { lat: latitude, lng: longitude } },
        (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
          if (status === 'OK' && results?.[0]) {
            const comp = results[0].address_components;
            const city = comp.find((c) =>
              c.types.includes('locality') || c.types.includes('administrative_area_level_1')
            );
            const country = comp.find((c) => c.types.includes('country'));
            const display = city && country ? `${city.long_name}, ${country.long_name}` : country?.long_name ?? '';

            dispatch(setLocationName(display));
            if (city) SetSessionStorageData(AUTHENTICATION.SHARED.REGION_NAME, city.long_name);
            if (country) SetSessionStorageData(AUTHENTICATION.SHARED.COUNTRY_NAME, country.long_name);
          }
        }
      );
    },
    [dispatch]
  );

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    if (dragTimeoutRef.current) clearTimeout(dragTimeoutRef.current);

    dragTimeoutRef.current = setTimeout(() => {
      if (!mapRef.current) return;
      const c = mapRef.current.getCenter();
      if (!c) return;

      const newLat = c.lat();
      const newLng = c.lng();

      const location = { lat: newLat, lng: newLng };
      setCurrentLocation(location);
      dispatch(setLocation(location));
      SetSessionStorageData(AUTHENTICATION.SHARED.SCHOOL_LOCATION, JSON.stringify(location));
      reverseGeocode(newLat, newLng);
    }, 400);
  }, [dispatch, reverseGeocode]);

  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  const handleDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  useEffect(() => {
    return () => {
      if (dragTimeoutRef.current) clearTimeout(dragTimeoutRef.current);
    };
  }, []);

  const profileIcon = useMemo(() => {
    if (
      isLoaded &&
      userProfileImg &&
      typeof userProfileImg === 'string' &&
      userProfileImg.trim() !== '' &&
      window.google?.maps
    ) {
      return {
        url: userProfileImg,
        scaledSize: new window.google.maps.Size(40, 40),
        anchor: new window.google.maps.Point(20, 20),
      };
    }
    return undefined;
  }, [isLoaded, userProfileImg]);

  return isLoaded ? (
    <div style={{ position: "relative", ...containerStyle }}>
      <div
        onMouseEnter={() => !isMobileView && setShowMapTooltip(true)}
        onMouseMove={(e: React.MouseEvent<HTMLDivElement>) => {
          if (!isMobileView) {
            const rect = e.currentTarget.getBoundingClientRect();
            setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
          }
        }}
        onMouseLeave={() => setShowMapTooltip(false)}
        onTouchStart={() => setShowMapTooltipMobile(false)}
      >
        <GoogleMap
          id={TUTORMAPCONSTANT.MAP__ID}
          mapContainerStyle={containerStyle}
          zoom={zoom}
          center={currentLocation || HeaderLocation}
          options={mapOptions}
          onClick={handleMapClick}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <CircleF
            center={currentLocation || HeaderLocation}
            radius={radius}
            options={{
              fillColor: "#178FFF",
              strokeColor: "rgba(79, 139, 255, 0.60)",
              strokeOpacity: 0.8,
              strokeWeight: 2,
              fillOpacity: 0.1,
            }}
          />

          {/* User's location marker with safe icon */}
          {HeaderLocation && (
            <MarkerF
              position={currentLocation || HeaderLocation}
              draggable={draggable}
              icon={profileIcon}
              onDragEnd={async (e: any) => {
                if (!e.latLng) return;
                const locationData = { lat: e.latLng.lat(), lng: e.latLng.lng() };

                setCurrentLocation(locationData);
                dispatch(setLocation(locationData));
                SetSessionStorageData(AUTHENTICATION.SHARED.SCHOOL_LOCATION, JSON.stringify(locationData));
                reverseGeocode(locationData.lat, locationData.lng);
              }}
              onClick={() => setShowMapTooltip(false)}
            />
          )}

          {/* School markers */}
          {SchoolList?.map((data: any) => {
            const position = {
              lat: parseFloat(data.latitude),
              lng: parseFloat(data.longitude),
            };

            const isActive = lat == data?.latitude && lang == data?.longitude;

            return (
              <Marker
                key={`${data.id}-school`}
                position={position}
                icon={{
                  url: SchoolMapicon,
                  scaledSize: new google.maps.Size(isActive ? 65 : 40, isActive ? 65 : 40),
                  anchor: new google.maps.Point(isActive ? 32.5 : 20, isActive ? 32.5 : 20),
                }}
                onMouseOver={() => setSelectedLocation(data)}
                onClick={() => setSelectedLocation(data)}
                animation={isActive ? google.maps.Animation.DROP : undefined}
              >
                {selectedLocation === data && (
                  <InfoWindow
                    onCloseClick={() => setSelectedLocation(null)}
                    options={{ maxWidth: 450 }}
                  >
                    <div
                      onClick={() => navigateDetailedView(data)}
                      onMouseEnter={() => setSelectedLocation(data)}
                      onMouseLeave={() => setSelectedLocation(null)}
                    >
                      <div
                        className="school_map_Hover__Card"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (clickableIcons && isMobileView) {
                            navigateDetailedView(data);
                          } else if (clickableIcons && getOneTutor) {
                            getOneTutor(data);
                          }
                        }}
                      >
                        <div className="rightside_contant">
                          <div className="w-100">
                            <div className="user__Detailes">
                              <div className="notranslate user__name" translate="no">
                                {language === "English"
                                  ? data?.school_name
                                  : data?.arabic_school_name || data?.school_name}
                              </div>
                              <div className="imageIcon" onClick={(e) => e.stopPropagation()}>
                                <WebShareComponent
                                  params={
                                    ROUTES.SCHOOL_DETAILE_VIEW + "?id=" + encryptPassData(data?.id)
                                  }
                                >
                                  <img src={shareIcon} alt="share" className="mb-1" />
                                </WebShareComponent>
                                <img
                                  src={data?.is_favourite ? SchoolFill : LikeIcon}
                                  alt="favorite"
                                  onClick={() => handleChangeFavourites(data?.is_favourite, data?.id)}
                                />
                              </div>
                            </div>
                            <div className="school_map_card_over_width">
                              <div className="school_map_user__Detailes__location">
                                <div className="School_infowindow__text" title={data?.rating}>
                                  {data?.rating ? data?.rating : GLOBAL_CONSTANTS.INITIAL_VALUES.NA}
                                </div>
                                <img src={locationIcon} alt="location" className="grayIcons ms-1" />
                                <div className="location_text" title={data?.city}>
                                  {data?.city}
                                </div>
                              </div>
                              <div className="mt-1 school_map_user__Detailes__location">
                                <img src={curriculum} alt="curriculum" />
                                <div className="lable curriculam_text" title={data?.curriculam}>
                                  {data?.curriculam}
                                </div>
                                <img src={grades} alt="grade" />
                                <div className="lable">
                                  {data?.grade ? data?.grade : "--"}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </InfoWindow>
                )}
              </Marker>
            );
          })}
        </GoogleMap>
      </div>

      {/* Desktop Tooltip */}
      {showMapTooltip && HeaderLocation && (
        <div
          style={{
            position: "absolute",
            left: tooltipPos.x,
            top: tooltipPos.y,
            zIndex: 1200,
            background: "#fff",
            padding: "6px 10px",
            borderRadius: 6,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            pointerEvents: "none",
            fontSize: 13,
            color: "#111",
          }}
        >
          {language === "English"
            ? "Drag the pin to select your preferred location"
            : "اسحب الدبوس لتحديد موقعك المفضل"}
        </div>
      )}

      {/* Mobile Tooltip */}
      {isMobileView && showMapTooltipMobile && (
        <div
          style={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            bottom: 12,
            zIndex: 1200,
            background: "rgba(255,255,255,0.95)",
            padding: "8px 12px",
            borderRadius: 8,
            boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
            pointerEvents: "none",
            fontSize: 13,
            color: "#111",
            maxWidth: "90%",
            textAlign: "center",
          }}
        >
          {language === "English"
            ? "Tap map to select preferred region or drag the marker"
            : "اضغط على الخريطة لاختيار المنطقة المفضلة أو اسحب العلامة"}
        </div>
      )}
    </div>
  ) : (
    <div>Loading map...</div>
  );
};

export default React.memo(SchoolMaps);